import { useState } from 'react'
import { useLazyQuery } from '@apollo/client'
import { showErrorToast, showSuccessToast } from '@npco/zeller-design-system'
import { GetContactImageUploadForm } from 'features/Contacts/contacts'

import {
  GetContactImageUploadForm as GetContactImageUploadFormPayload,
  GetContactImageUploadFormVariables,
} from 'types/gql-types/GetContactImageUploadForm'
import { ErrorLogger } from 'services/ErrorLogger/ErrorLogger'
import { page } from 'translations'

interface FetchUploadImageToS3BucketProps {
  imageUrl: string
  formFields: Record<string, string>
  onError: () => void
  onSuccess: () => void
  s3BucketUrl: string
}

export const fetchUploadImageToS3Bucket = async ({
  imageUrl,
  formFields,
  onError,
  onSuccess,
  s3BucketUrl,
}: FetchUploadImageToS3BucketProps) => {
  try {
    const formData = new FormData()

    // NOTE: attach form fields with authorization params from aws
    Object.keys(formFields).forEach((prop) =>
      formData.append(prop, formFields[prop])
    )

    // NOTE: using fetch to make the process of converting to blob more straight
    // forward. The backend must receive the image in a binary format as
    // otherwise this won't work as expected.
    const response = await fetch(imageUrl, { method: 'GET' })
    const blob = await response.blob()

    formData.append('file', blob)

    await fetch(s3BucketUrl, {
      method: 'POST',
      body: formData,
    })
    onSuccess()
  } catch (err) {
    ErrorLogger.report('[Core] Upload to bucket', err)
    onError()
  }
}

export interface UseS3BucketForProfilePictureProps {
  imageUrl: string
  onImageUploaded: (url: string) => void
  closeModal: () => void
}

export const useS3BucketForProfilePicture = ({
  imageUrl,
  onImageUploaded,
  closeModal,
}: UseS3BucketForProfilePictureProps) => {
  const [isUploadingFileToS3, setIsUploadingFileToS3] = useState(false)

  const [upload] = useLazyQuery<
    GetContactImageUploadFormPayload,
    GetContactImageUploadFormVariables
  >(GetContactImageUploadForm, {
    fetchPolicy: 'network-only',
    onCompleted: (responseData: GetContactImageUploadFormPayload) => {
      fetchUploadImageToS3Bucket({
        formFields: JSON.parse(
          responseData.getContactImageUploadForm.formFields
        ),
        imageUrl,
        onSuccess: () => {
          setIsUploadingFileToS3(false)
          showSuccessToast(page.contact.sections.imageUpload.successCopy)
          onImageUploaded(imageUrl)
          closeModal()
        },
        onError: () => {
          setIsUploadingFileToS3(false)
          showErrorToast(page.contact.sections.imageUpload.failureCopy)
          closeModal()
        },
        s3BucketUrl: responseData.getContactImageUploadForm.url,
      })
    },
    onError: () => {
      showErrorToast(page.contact.sections.imageUpload.failureCopy)
      setIsUploadingFileToS3(false)
      closeModal()
    },
  })

  const uploadFileToS3Bucket = (contactUuid: string) => {
    setIsUploadingFileToS3(true)
    upload({
      variables: {
        contactUuid,
      },
    })
  }

  return {
    isUploadingFileToS3,
    uploadFileToS3Bucket,
  }
}
